import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { compareSortValues } from '@bp-files/ui';
import { ApiError } from '@bp-files/client';
import { getFolderDestinations, type FolderDestination } from '@/lib/api';

export type DisplayFolderDestination = FolderDestination & { displayName: string };

let version = 0;
const listeners = new Set<() => void>();

/** Refetch persistent destination lists after an admin changes folder structure. */
export function folderDestinationsChanged(): void {
  version += 1;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getVersion(): number {
  return version;
}

/**
 * The destination's visible name — a **row-local** rule (FIL-1d, FIL-1e).
 *
 * A space's root reads as the space; every folder reads as `Space — Folder`.
 * This used to count each space's folders and drop the folder name where there
 * was exactly one, which meant a destination renamed itself the moment somebody
 * created a second folder beside it. Nothing here looks at the list any more,
 * and the sort is what remains of the set.
 *
 * Sorting on the derived name is what puts a space's root immediately above its
 * own folders: the root's name is a prefix of theirs.
 */
export function withDisplayNames(folders: FolderDestination[]): DisplayFolderDestination[] {
  return folders
    .map((folder) => ({
      ...folder,
      displayName: folder.isRoot ? folder.spaceName : `${folder.spaceName} — ${folder.folderName}`,
    }))
    .sort(
      (a, b) =>
        compareSortValues(a.displayName, b.displayName) ||
        compareSortValues(a.spaceId, b.spaceId) ||
        compareSortValues(a.folderId, b.folderId),
    );
}

export type FolderDestinationsState =
  | { kind: 'loading' }
  | { kind: 'loaded'; folders: DisplayFolderDestination[] }
  | { kind: 'error'; status: number };

export function useFolderDestinations(): {
  state: FolderDestinationsState;
  retry: () => void;
} {
  const [state, setState] = useState<FolderDestinationsState>({ kind: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const changed = useSyncExternalStore(subscribe, getVersion, getVersion);

  useEffect(() => {
    const controller = new AbortController();

    void getFolderDestinations(controller.signal)
      .then(({ folders }) => {
        setState({ kind: 'loaded', folders: withDisplayNames(folders) });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState({ kind: 'error', status: error instanceof ApiError ? error.status : 0 });
      });

    return () => {
      controller.abort();
    };
  }, [reloadToken, changed]);

  const retry = useCallback(() => {
    setState({ kind: 'loading' });
    setReloadToken((token) => token + 1);
  }, []);

  return { state, retry };
}
